<script lang="ts">
  import { onMount, afterUpdate } from "svelte";
  import Icon from "$lib/components/Icon/Icon.svelte";
  export let gradientHeight = "100%";
  export let rightGradientOffset = 0;
  export let hasHeightOffset = true;
  export let arrows = true;
  export let invert = false;

  let contentElement: HTMLElement | null = null;
  let containerElement: HTMLElement | null = null;
  let isScrollable = false;
  let showLeftGradient = false;
  let showRightGradient = false;
  let heightOffset = gradientHeight;

  $: if (hasHeightOffset) {
    heightOffset = gradientHeight;
  } else {
    heightOffset = "0px";
  }

  $: leftGradientStyles = `height:${gradientHeight};transform: translateY(-${heightOffset}) `;
  $: rightGradientStyles = `height:${gradientHeight}; transform: translateY(-${heightOffset}) translateX(-${rightGradientOffset}px);`;

  $: leftArrowStyles = `height: ${gradientHeight}; bottom: 0; transform: translateX(${
    gradientHeight === "28px" ? "-16px" : "0px"
  });`;

  $: rightArrowStyles = `height: ${gradientHeight}; bottom: 0;`;

  onMount(() => {
    containerElement?.addEventListener("scroll", handleScroll);
    checkOverflow();
  });

  afterUpdate(() => {
    // After each update, recheck the overflow
    checkOverflow();
  });

  // Listen for scroll events
  function handleScroll() {
    checkOverflow();
  }
  // Check if the content overflows horizontally
  function checkOverflow() {
    if (contentElement && containerElement) {
      isScrollable = contentElement.scrollWidth > containerElement.clientWidth;
      showLeftGradient = containerElement.scrollLeft > 0;
      showRightGradient =
        contentElement.scrollWidth - containerElement.clientWidth >
        containerElement.scrollLeft + 1;
    }
  }

  function handleScrollLeft() {
    containerElement.scrollBy({
      top: 0,
      left: 0 - containerElement.clientWidth / 3,
      behavior: "smooth",
    });
  }

  function handleScrollRight() {
    containerElement.scrollBy({
      top: 0,
      left: containerElement.clientWidth / 3,
      behavior: "smooth",
    });
  }
</script>

<style lang="scss">
  .scroll-container {
    width: 100%;
    overflow: auto;
    position: relative;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .content {
    width: max-content;
    white-space: nowrap;
    overflow: auto;
    position: relative;
  }

  .gradient-left,
  .gradient-right {
    position: absolute;
    width: 72px;
    pointer-events: none; /* Allow interactions with the slot content */
    scroll-padding: 20px;
    z-index: 20;
    display: flex;
    align-items: center;
  }

  .gradient-left {
    background-image: linear-gradient(
      to left,
      rgba(255, 255, 255, 0),
      $surface
    );

    &--invert {
      background-image: linear-gradient(
        to left,
        rgba(255, 255, 255, 0),
        $inverse-surface
      );
    }
  }

  .gradient-right {
    right: 0;
    background-image: linear-gradient(
      to right,
      rgba(255, 255, 255, 0),
      $surface
    );

    &--invert {
      background-image: linear-gradient(
        to right,
        rgba(255, 255, 255, 0),
        $inverse-surface
      );
    }
  }

  .arrow {
    position: absolute;
    background-color: transparent;
    border: none;
    pointer-events: auto;

    &--left {
      left: 0;
    }

    &--right {
      right: 0;
    }
  }
</style>

<div
  class="scroll-container"
  on:scroll={handleScroll}
  bind:this={containerElement}
>
  <div class="content" bind:this={contentElement}>
    <slot />
  </div>
</div>
{#if isScrollable}
  {#if showLeftGradient}
    <div
      class="gradient-left {invert ? 'gradient-left--invert' : ''}"
      style={leftGradientStyles}
    >
      {#if arrows}
        <button
          class="arrow arrow--left"
          style={leftArrowStyles}
          on:click={handleScrollLeft}
        >
          <Icon icon="chevron_left" />
        </button>
      {/if}
    </div>
  {/if}
  {#if showRightGradient}
    <div
      class="gradient-right {invert ? 'gradient-right--invert' : ''}"
      style={rightGradientStyles}
    >
      {#if arrows}
        <button
          class="arrow arrow--right"
          style={rightArrowStyles}
          on:click={handleScrollRight}
        >
          <Icon icon="chevron_right" />
        </button>
      {/if}
    </div>
  {/if}
{/if}
